/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { CustomModal } from './google-map-modal';
import { AddressListModal } from './address-list-modal';
import { observer } from 'mobx-react';
import React from 'react';
import { Button } from '@msdyn365-commerce-modules/utilities';

export interface ILocationSelectorProps {
    isModalOpen: any;
    toggleMapModal: () => void;
    setSelectedMarker: (marker: any) => void;
    setAddressBarDetails: (data: string) => void;
    mapApiKey: string;
    selectedMarker: any;
    address: any;
    addressPurposes: any;
    addressBarDetails: string;
    setActiveModalContent: (data: string) => void;
    activeModalContent: string;
    selectedAddress: any;
    setSelectedAddress: (data: any) => void;
}

/**
 *
 * LocationSelector component.
 * @extends {React.PureComponent<ILocationSelectorProps>}
 */

@observer
export class LocationSelector extends React.PureComponent<ILocationSelectorProps> {
    constructor(props: ILocationSelectorProps) {
        super(props);
    }

    public componentDidMount() {
        // Show current location pin details
        if (this.props.selectedMarker) {
            if (this.props.selectedMarker?.name) {
                this.props.setAddressBarDetails(this.props.selectedMarker.name);
            } else if (this.props.selectedMarker?.address_name) {
                this.props.setAddressBarDetails(this.props.selectedMarker.address_name);
            }
        }

        if (this.props.address && this.props.address.length > 0) {
            this.props.setActiveModalContent('address');
        }
        // Show Nearest address location pin details if no saved address in LS & if no location pin selected by user
        if (
            this.props.address &&
            this.props.address.length > 0 &&
            this.props.selectedAddress === 0 &&
            this.props.addressBarDetails !== 'Select location'
        ) {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    position => {
                        // Calculate the distance between the user and each address
                        const sortedAddressObject = this.props.address.map((addr: any) => {
                            const latLng = addr.DistrictName.split(',');
                            const dist = this.fetchDistance(latLng[0], latLng[1], position.coords.latitude, position.coords.longitude);
                            return { distance: dist, ...addr };
                        });

                        // Sort by distance nearest address
                        sortedAddressObject.sort(
                            (a: { distance: string }, b: { distance: string }) => parseFloat(a.distance) - parseFloat(b.distance)
                        );

                        // console.log('thiss Distance (km): ', sortedAddressObject);
                        if (sortedAddressObject && sortedAddressObject[0]) {
                            const addressType = this.props.addressPurposes.filter(
                                (addressTypeValue: { AddressType: any }) =>
                                    addressTypeValue.AddressType === sortedAddressObject[0].AddressTypeValue
                            );
                            const addressTypeValue = addressType.length > 0 ? addressType[0].Description : '';
                            this.props.setActiveModalContent('address');
                            this.props.setAddressBarDetails(`${addressTypeValue} - ${sortedAddressObject[0].Street}`);
                            this.props.setSelectedAddress(sortedAddressObject[0].RecordId);
                        }
                    },
                    error => {
                        console.error('Error getting user location:', error);
                    }
                );
            }
        }
        // Show selected address from LS here
        if (this.props.selectedAddress !== 0) {
            const selectAddDetails = this.props.address.filter((item: { RecordId: any }) => item.RecordId === this.props.selectedAddress);
            if (selectAddDetails && selectAddDetails.length > 0) {
                const addressType = this.props.addressPurposes.filter(
                    (addressTypeValue: { AddressType: any }) => addressTypeValue.AddressType === selectAddDetails[0].AddressTypeValue
                );
                const addressTypeValue = addressType.length > 0 ? addressType[0].Description : '';
                this.props.setAddressBarDetails(`${addressTypeValue} - ${selectAddDetails[0].Street}`);
                this.props.setActiveModalContent('address');
            }
        }
    }

    // Haversine formula in JavaScript
    public fetchDistance(lat: number, lon: number, currentLat: number, currentLng: number) {
        const R = 6371; // Radius of the earth in km
        const dLat = (lat - currentLat) * (Math.PI / 180);
        const dLon = (lon - currentLng) * (Math.PI / 180);
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos((currentLat * Math.PI) / 180) * Math.cos((lat * Math.PI) / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    }
    ///
    public render(): JSX.Element {
        return (
            <>
                <Button className='ms-location-selector' onClick={this.props.toggleMapModal}>
                    <p className='ms-wishlist-text-new'>Deliver here</p>
                    <svg width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
                        <path
                            d='M11.5 5C11.5 7.49 7 13.5 7 13.5C7 13.5 2.5 7.49 2.5 5C2.5 3.80653 2.97411 2.66193 3.81802 1.81802C4.66193 0.974106 5.80653 0.5 7 0.5C8.19347 0.5 9.33807 0.974106 10.182 1.81802C11.0259 2.66193 11.5 3.80653 11.5 5V5Z'
                            stroke='black'
                            stroke-linecap='round'
                            stroke-linejoin='round'
                        />
                        <path
                            d='M7 6.5C7.82843 6.5 8.5 5.82843 8.5 5C8.5 4.17157 7.82843 3.5 7 3.5C6.17157 3.5 5.5 4.17157 5.5 5C5.5 5.82843 6.17157 6.5 7 6.5Z'
                            stroke='black'
                            stroke-linecap='round'
                            stroke-linejoin='round'
                        />
                    </svg>

                    <span className=''>{`${this.props.addressBarDetails.substring(0, 20)}...`}</span>
                    <img src='https://files-eu-prod.cms.commerce.dynamics.com/cms/api/vnnhvggtsx/binary/MD8qC' className='drop-arrow'></img>
                </Button>
                {this.props.isModalOpen && this.props.activeModalContent === 'location' && (
                    <CustomModal
                        isOpen={this.props.isModalOpen}
                        onRequestClose={this.props.toggleMapModal}
                        setSelectedMarker={this.props.setSelectedMarker}
                        mapApiKey={this.props.mapApiKey}
                        setActiveModalContent={this.props.setActiveModalContent}
                        activeModalContent={this.props.activeModalContent}
                        setAddressBarDetails={this.props.setAddressBarDetails}
                        address={this.props.address}
                    />
                )}
                {this.props.isModalOpen && this.props.activeModalContent === 'address' && (
                    <AddressListModal
                        isOpen={this.props.isModalOpen}
                        onRequestClose={this.props.toggleMapModal}
                        addressData={this.props.address}
                        setActiveModalContent={this.props.setActiveModalContent}
                        activeModalContent={this.props.activeModalContent}
                        addressPurposes={this.props.addressPurposes}
                        selectedAddress={this.props.selectedAddress}
                        setSelectedAddress={this.props.setSelectedAddress}
                        setAddressBarDetails={this.props.setAddressBarDetails}
                    />
                )}
            </>
        );
    }
}
